import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { DateRangeBar, useDateRange } from '../components/DateRange';
import { usePaged } from '../lib/usePaged';
import { usePosDataSource } from '../lib/posData/usePosData';
import { dateTime, money } from '../lib/format';
import { PageHeader } from '../components/Layout';
import { EmailInvoiceModal } from '../components/EmailInvoiceModal';
import { InvoiceDoc } from '../components/InvoiceDoc';
import { PaperWidthPicker, ThermalPrintButton } from '../components/ThermalPrint';
import { invoiceReceipt } from '../lib/receipt';
import {
  Empty,
  ErrorState,
  Loading,
  Modal,
  Pager,
  Select,
  StatusChip,
  TextInput,
  useToast,
} from '../components/ui';


/** The most the register asks for at once; a range with more says so, and can be narrowed. */
const FETCH_LIMIT = 500;

export function InvoicesPage() {
  const queryClient = useQueryClient();
  const { fail, push } = useToast();
  const { can } = useAuth();
  const [kind, setKind] = useState<'' | 'FNB' | 'ROOM'>('');
  const [search, setSearch] = useState('');
  // Today by default: a register that opens on every invoice ever raised is a long
  // wait and a longer scroll, and the question asked of it is nearly always "today".
  const range = useDateRange('today');
  const { preset, from, to } = range;
  const [status, setStatus] = useState<'' | 'PAID' | 'VOID'>('');
  const [mode, setMode] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [voiding, setVoiding] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [reason, setReason] = useState('');

  // Same seam as the POS page: API in the browser, local SQLite on the
  // kiosk. In local mode the register shows this till's own window of
  // invoices (docs/offline/local-dataset.md retention), and voiding stays
  // an online operation until sync exists.
  const { source, isLoading: sourceLoading, error: sourceError, retry: retrySource } = usePosDataSource();

  const invoices = useQuery({
    queryKey: ['invoices', kind, search, from, to],
    queryFn: () =>
      source!.listInvoices({
        kind: kind === '' ? undefined : kind,
        q: search,
        from: from || undefined,
        to: to || undefined,
        limit: FETCH_LIMIT,
      }),
    // Keep what is on screen while the next range loads, instead of swapping the
    // whole page for a spinner (which also takes focus out of the box being typed in).
    placeholderData: keepPreviousData,
    enabled: Boolean(source),
  });

  const invoice = useQuery({
    queryKey: ['invoice', openId],
    queryFn: () => source!.getInvoice(openId!),
    enabled: Boolean(source && openId),
  });

  // Status and payment mode narrow what was fetched; the dates and search are the server's.
  const rows = (invoices.data ?? []).filter(
    (row) => (status === '' || row.status === status) && (mode === '' || row.mode === mode),
  );
  const paged = usePaged(rows, { phone: 10, desktop: 25 });

  const voidInvoice = useMutation({
    mutationFn: () => source!.voidInvoice(openId!, reason),
    onSuccess: () => {
      push('Invoice voided and stock restored', 'ok');
      setVoiding(false);
      setReason('');
      setOpenId(null);
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: fail,
  });

  if (sourceLoading || invoices.isLoading) return <Loading label="Loading the register…" />;
  if (sourceError) return <ErrorState error={sourceError} onRetry={retrySource} />;
  if (invoices.error)
    return <ErrorState error={invoices.error} onRetry={() => void invoices.refetch()} />;

  const modes = [...new Set((invoices.data ?? []).map((row) => row.mode))].sort();
  const narrowed = !range.isInitial || status !== '' || mode !== '' || kind !== '' || search !== '';
  const settled = rows.filter((row) => row.status !== 'VOID');
  const total = settled.reduce((sum, row) => sum + row.total, 0);

  return (
    <div className="stack">
      <PageHeader title="Invoices" subtitle="Every settled bill, restaurant and rooms" />

      <div className="card">
        <div className="card-head">
          <div className="seg">
            {[
              { id: '', label: 'All' },
              { id: 'FNB', label: 'Restaurant' },
              { id: 'ROOM', label: 'Rooms' },
            ].map((option) => (
              <button
                key={option.id}
                className={option.id === kind ? 'active' : ''}
                onClick={() => setKind(option.id as '' | 'FNB' | 'ROOM')}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="search pos-search">
            <input
              className="input"
              placeholder="Invoice number or guest"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="filter-bar">
          <DateRangeBar range={range} />
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="">Any status</option>
            <option value="PAID">Paid</option>
            <option value="VOID">Void</option>
          </Select>
          <Select label="Payment" value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="">Any payment</option>
            {modes.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
          {narrowed ? (
            <button
              className="btn ghost"
              onClick={() => {
                range.choose('today');
                setStatus('');
                setMode('');
                setKind('');
                setSearch('');
              }}
            >
              Reset to today
            </button>
          ) : null}
        </div>

        {(invoices.data ?? []).length >= FETCH_LIMIT ? (
          <div className="form-note warn" style={{ margin: '0 14px 12px' }}>
            Showing the latest {FETCH_LIMIT} invoices for these dates. Narrow the dates to see the rest.
          </div>
        ) : null}

        {rows.length === 0 ? (
          <div>
            <Empty
              icon="❑"
              message={preset === 'today' ? 'No invoices raised today' : 'No invoices match these filters'}
            />
            {preset !== 'all' ? (
              <div className="row gap-6" style={{ justifyContent: 'center', paddingBottom: 22 }}>
                <button className="btn" onClick={() => range.choose('30d')}>
                  Show the last 30 days
                </button>
                <button className="btn" onClick={() => range.choose('all')}>
                  Show every date
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="tbl tbl-stack">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Issued</th>
                  <th>Guest</th>
                  <th>Source</th>
                  <th className="num">Taxable</th>
                  <th className="num">GST</th>
                  <th className="num">Total</th>
                  <th>Mode</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((row) => (
                  <tr key={row.id} className="clickable" onClick={() => setOpenId(row.id)}>
                    <td className="b mono">{row.number}</td>
                    <td className="nowrap">{dateTime(row.issuedAt)}</td>
                    <td>{row.guestName}</td>
                    <td>
                      {row.kind === 'ROOM' ? 'Room folio' : (row.ticketNumber ?? 'Restaurant')}
                    </td>
                    <td className="num">{money(row.subtotal)}</td>
                    <td className="num dim">{money(row.tax)}</td>
                    <td className="num b">{money(row.total)}</td>
                    <td>{row.mode}</td>
                    <td>
                      <StatusChip status={row.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6}>{settled.length} settled invoice(s)</td>
                  <td className="num" data-label="Total">
                    {money(total)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <Pager {...paged} noun="invoices" onPage={paged.setPage} />
      </div>

      {openId ? (
        <Modal
          title={invoice.data ? `Invoice ${invoice.data.number}` : 'Invoice'}
          size="wide"
          onClose={() => setOpenId(null)}
          footer={
            <>
              <PaperWidthPicker />
              <button className="btn" onClick={() => setOpenId(null)}>
                Close
              </button>
              {can('invoice:void') &&
              invoice.data?.status !== 'VOID' &&
              invoice.data?.kind === 'FNB' ? (
                <button className="btn danger" onClick={() => setVoiding(true)}>
                  Void invoice
                </button>
              ) : null}
              {/* Online only: a till's own database has no address to send to and no
                  mail server, so its invoices do not carry the hint that shows this. */}
              {can('invoice:email') &&
              invoice.data &&
              invoice.data.status !== 'VOID' &&
              invoice.data.customerEmailHint !== undefined ? (
                <button className="btn" onClick={() => setEmailing(true)}>
                  Email invoice
                </button>
              ) : null}
              <button className="btn" onClick={() => window.print()}>
                A4 invoice
              </button>
              {invoice.data ? (
                <ThermalPrintButton
                  data={invoiceReceipt(invoice.data)}
                  className="btn primary"
                  label="Print receipt"
                />
              ) : null}
            </>
          }
        >
          {invoice.isLoading || !invoice.data ? <Loading /> : <InvoiceDoc invoice={invoice.data} />}
        </Modal>
      ) : null}

      {emailing && invoice.data ? (
        <EmailInvoiceModal invoice={invoice.data} onClose={() => setEmailing(false)} />
      ) : null}

      {voiding ? (
        <Modal
          title="Void invoice"
          size="narrow"
          onClose={() => setVoiding(false)}
          footer={
            <>
              <button className="btn" onClick={() => setVoiding(false)}>
                Cancel
              </button>
              <button
                className="btn danger"
                disabled={!reason.trim() || voidInvoice.isPending}
                onClick={() => voidInvoice.mutate()}
              >
                Void and restore stock
              </button>
            </>
          }
        >
          <p className="dim" style={{ marginBottom: 14 }}>
            The invoice is cancelled, the stock it consumed is written back, and both are recorded
            in the audit log. The invoice number is never reused.
          </p>
          <TextInput
            label="Reason"
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Modal>
      ) : null}
    </div>
  );
}
